import PropTypes from 'prop-types';
import { Component } from 'react';
import injectSheet from 'react-jss';

import { BULK_EDIT_LIMIT } from '~/common/constants/max_lengths';
import { Button } from '~/eds/src/components/Button/Button';
import { DateInput } from '~/eds/src/components/DateInput/DateInput';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { parseDate } from '~/eds/src/utils/parseDate';
import { DataFieldType } from '~/enums/DataFieldType';
import { FlagType } from '~/flags/flags';
import { withFlags } from '~/flags/withFlags';
import { injectIntl } from 'react-intl';
import { withFormatDate } from '~/i18n/hocs/withFormatDate';
import Alert from '~/ui/components/Alert/Alert';
import Box from '~/ui/components/Box/Box';
import MultiSelect from '~/ui/components/MultiSelect/MultiSelect';

import { ERROR, SUCCESS } from '../../../types/toast.types';
import EcDataField from '../../Shared/EcDataField/EcDataField';
import EcDatepicker from '../../Shared/EcDatepicker/EcDatepicker';
import EcModalCard from '../../Shared/EcModalCard/EcModalCard';
import EcRadioButton from '../../Shared/EcRadioButton/EcRadioButton';
import EcRadioContainer from '../../Shared/EcRadioContainer/EcRadioContainer';
import { showToast } from '../../Shared/EcToast/showToast';
import LoadingSpinner from '../../Shared/Icons/LoadingSpinner';
import styles from './MultiEditModal.styles';

class MultiEditModal extends Component {
  constructor(props) {
    super(props);

    this.handleOnAddFieldChange = this.handleOnAddFieldChange.bind(this);
    this.handleOnFieldChange = this.handleOnFieldChange.bind(this);
    this.handleApplyChangesClick = this.handleApplyChangesClick.bind(this);

    this.state = {
      fieldOptions: [],
      fields: [],
      loading: true,
      overServerLimit: false,
    };
  }

  async componentDidMount() {
    const { getModalData, hideModal, fetchDocumentsCount, overLimit, intl } =
      this.props;
    const defaultFetchDocuments = () => Promise.resolve({});
    const fetchCount =
      fetchDocumentsCount && !overLimit
        ? fetchDocumentsCount
        : defaultFetchDocuments;
    try {
      const [{ fields }, { document_count }] = await Promise.all([
        getModalData(),
        fetchCount(),
      ]);
      const editableFields = fields.filter((field) => field.is_editable);
      this.setState({
        loading: false,
        fieldOptions: editableFields,
        overServerLimit: document_count > BULK_EDIT_LIMIT,
      });
    } catch {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'MultiEditModal.editFieldsError',
          defaultMessage: 'Something went wrong with editing fields.',
          description: 'Error message for editing fields',
        }),
      );
      hideModal();
    }
  }

  static processBulkData(data) {
    return data.map((f) => {
      let value;
      if (Array.isArray(f.value)) {
        value = f.value.map((v) => v.value);
      } else if (f.value && f.value.hasOwnProperty('value')) {
        value = f.value.value;
      } else {
        value = f.value;
      }

      return {
        id: f.id,
        document_ids: f.document_ids,
        keep_old_values: f.keep_old_values,
        value,
      };
    });
  }

  generateNewFields(fieldId, valueObject) {
    const fieldsCopy = [...this.state.fields];
    const currentIndex = fieldsCopy.findIndex((f) => f.id === Number(fieldId));
    fieldsCopy[currentIndex] = { ...fieldsCopy[currentIndex], ...valueObject };

    return fieldsCopy;
  }

  handleOnAddFieldChange(labels) {
    const fields = labels.map((label) => {
      const { fields, fieldOptions } = this.state;
      return [...fields, ...fieldOptions].find((opt) => opt.label === label);
    });

    this.setState({ fields: fields }, () => {
      if (this.state.fields.length > 1) {
        document
          .getElementById([...this.state.fields].pop().id)
          .scrollIntoView();
      }
    });
  }

  handleOnFieldChange(newState) {
    const editedFieldId = Object.keys(newState)[0];
    const fields = this.generateNewFields(editedFieldId, {
      value: newState[editedFieldId],
    });

    this.setState({ fields });
  }

  handleOnRemoveClick({ id }) {
    const newFields = this.state.fields.filter((field) => field.id !== id);
    this.setState({ fields: newFields });
  }

  handleDateChange(date, fieldId) {
    const fields = this.generateNewFields(fieldId, { value: date });
    this.setState({ fields });
  }

  handleApplyChangesClick() {
    const { update, onUpdateComplete, hideModal, intl } = this.props;
    const { fields } = this.state;

    const bulkData = MultiEditModal.processBulkData(fields);

    this.setState({ loading: true });
    update(bulkData)
      .then((response) => {
        onUpdateComplete();
        showToast(
          SUCCESS,
          (response && response.detail) ??
            intl.formatMessage({
              id: 'MultiEditModal.documentsEditedSuccess',
              defaultMessage: 'Documents successfully edited.',
              description: 'Success message for editing documents',
            }),
        );
        hideModal();
      })
      .catch((error) => {
        const genericMessage = intl.formatMessage({
          id: 'MultiEditModal.editFieldsGenericError',
          defaultMessage: 'Something went wrong with editing fields.',
          description: 'Generic error message for editing fields',
        });
        const errorMessage = error?.response?.data?.error || genericMessage;
        showToast(ERROR, errorMessage);
        hideModal();
      });
  }

  onCalendarOpenClose = (open) => {
    this.props.disableEscapeKey(open);
  };

  renderExtraFieldOptions(field) {
    const { classes, intl } = this.props;

    return (
      <EcRadioContainer
        groupName={`${field.id}_multiple_radio_options`}
        defaultValue={field.keep_old_values}
        onChange={({ selectedValue }) => {
          const fields = this.generateNewFields(field.id, {
            keep_old_values: selectedValue,
          });
          this.setState({ fields });
        }}
      >
        {(groupName, selectedValue, onOptionChange) => (
          <div className={classes.fieldActions}>
            <EcRadioButton
              id={`${field.id}-replace_existing_values`}
              isInline={true}
              groupName={groupName}
              value={false}
              label={intl.formatMessage({
                id: 'MultiEditModal.replaceExistingValues',
                defaultMessage: 'Replace existing values',
                description: 'Radio button label for replacing existing values',
              })}
              onChange={onOptionChange}
              selectedValue={selectedValue}
            />
            <EcRadioButton
              id={`${field.id}-add_existing_values`}
              isInline={true}
              groupName={groupName}
              value={true}
              label={intl.formatMessage({
                id: 'MultiEditModal.addExistingValues',
                defaultMessage: 'Add to existing values',
                description: 'Radio button label for adding to existing values',
              })}
              onChange={onOptionChange}
              selectedValue={selectedValue}
            />
          </div>
        )}
      </EcRadioContainer>
    );
  }

  renderField(field) {
    const { classes, flags, intl, formatDate } = this.props;
    const enableLocalization = flags[FlagType.LanguageSelect];

    const placeholder = intl.formatMessage(
      {
        id: 'MultiEditModal.enterFieldPlaceholder',
        defaultMessage: 'Enter {fieldLabel}',
        description:
          'Placeholder text for data field input. Example: fieldLabel = "Contract Date"',
      },
      { fieldLabel: field.label },
    );

    if (field.type === DataFieldType.TEXT_AREA) {
      return (
        <div className={classes.textAreaFieldContainer}>
          <EcDataField
            field={field}
            fieldValue={field.value}
            onFieldChange={this.handleOnFieldChange}
            fieldPlaceholder={[placeholder]}
          />
        </div>
      );
    }

    return (
      <div className={classes.fieldContainer}>
        {field.type === DataFieldType.DATE ? (
          enableLocalization ? (
            <Layout>
              <DateInput
                value={field.value ? parseDate(field.value) : null}
                onChange={(date) => {
                  const newDate = date ? formatDate(date, 'iso') : null;
                  this.handleDateChange(newDate, field.id);
                }}
              />
              {field.is_nullable && (
                <Button
                  level="tertiary"
                  text={intl.formatMessage({
                    id: 'MultiEditModal.clearDate',
                    defaultMessage: 'Clear',
                    description: 'Clear date button text',
                  })}
                  onClick={() => {
                    this.handleDateChange(null, field.id);
                  }}
                />
              )}
            </Layout>
          ) : (
            <EcDatepicker
              date={field.value ? parseDate(field.value) : null}
              isInModal={true}
              onDateChange={(date) => {
                const newDate = date ? formatDate(date, 'iso') : null;
                this.handleDateChange(newDate, field.id);
              }}
              onCalendarOpenClose={this.onCalendarOpenClose}
              placeholder={placeholder}
              isClearable={field.is_nullable}
            />
          )
        ) : (
          <EcDataField
            field={field}
            fieldValue={field.value}
            onFieldChange={this.handleOnFieldChange}
            fieldPlaceholder={placeholder}
          />
        )}
      </div>
    );
  }

  renderFieldSection(field) {
    const { classes, intl } = this.props;

    return (
      <div className={classes.fieldSectionWrapper} key={field.id} id={field.id}>
        <div className={classes.fieldSection}>
          <div className={classes.fieldLabel}>
            <span>{field.label}</span>
            <IconButton
              tooltip={intl.formatMessage({
                id: 'MultiEditModal.removeTooltip',
                defaultMessage: 'Remove',
                description: 'Tooltip for remove field button',
              })}
              text={intl.formatMessage({
                id: 'MultiEditModal.removeFieldText',
                defaultMessage: 'Remove field',
                description: 'Text for remove field button',
              })}
              onClick={() => this.handleOnRemoveClick(field)}
              icon="x"
            />
          </div>
          {this.renderField(field)}
          {field.type === DataFieldType.ARRAY_MULTIPLE &&
            this.renderExtraFieldOptions(field)}
        </div>
      </div>
    );
  }

  renderLimitWarning() {
    const { overLimit, intl } = this.props;
    const { overServerLimit } = this.state;

    if (overLimit || overServerLimit) {
      return (
        <Box mb={5}>
          <Alert enableIcon variant="warning">
            {intl.formatMessage(
              {
                id: 'MultiEditModal.bulkEditLimitWarning',
                defaultMessage:
                  'Only {limit} documents can be edited at a time. Please select fewer documents to complete this action.',
                description:
                  'Warning message for bulk edit limit. Example: limit = "100"',
              },
              { limit: BULK_EDIT_LIMIT },
            )}
          </Alert>
        </Box>
      );
    } else {
      return null;
    }
  }

  render() {
    const { classes, hideModal, overLimit, intl } = this.props;
    const { fields, loading, fieldOptions, overServerLimit } = this.state;
    const fieldOptionsMapped = fieldOptions.map((opt) => ({
      value: opt.label,
      ...opt,
    }));

    const modalTitle = intl.formatMessage({
      id: 'MultiEditModal.title',
      defaultMessage: 'Multi Edit',
      description: 'Modal title for multi edit',
    });

    const invalidFields = fields
      .filter((f) => !f.value || (Array.isArray(f.value) && !f.value.length))
      .map((f) => f.label);

    return (
      <EcModalCard
        title={modalTitle}
        content={
          <>
            {loading ? (
              <div className={classes.loadingContainer}>
                <LoadingSpinner size="medium" />
              </div>
            ) : (
              <div className={classes.contentContainer}>
                {this.renderLimitWarning()}
                <div className={classes.addFieldSection}>
                  <div className={classes.fieldLabel}>
                    {intl.formatMessage({
                      id: 'MultiEditModal.addDataFieldsTitle',
                      defaultMessage: 'Add Data Fields',
                      description: 'Title for add data fields section',
                    })}
                  </div>
                  <div className={classes.fieldDescription}>
                    {intl.formatMessage({
                      id: 'MultiEditModal.addDataFieldsDescription',
                      defaultMessage:
                        'You can specify which data fields you want to update across the selected document(s).',
                      description: 'Description for add data fields section',
                    })}
                  </div>
                  <div className={classes.fieldContainer}>
                    <MultiSelect
                      values={fields.map((f) => f.label)}
                      options={fieldOptionsMapped}
                      width="fullWidth"
                      onChange={this.handleOnAddFieldChange}
                      placeholder={intl.formatMessage({
                        id: 'MultiEditModal.searchPlaceholder',
                        defaultMessage: 'Search for values...',
                        description: 'Placeholder text for search input',
                      })}
                      isClearable={false}
                    />
                  </div>
                </div>
                <div className={classes.contentDivider} />
                {fields.map((field) => this.renderFieldSection(field))}
              </div>
            )}
          </>
        }
        footer={
          <>
            <Button
              variant="tertiary"
              text={intl.formatMessage({
                id: 'MultiEditModal.cancelButton',
                defaultMessage: 'Cancel',
                description: 'Cancel button text',
              })}
              onClick={hideModal}
            />
            <Button
              tooltip={
                invalidFields.length ? (
                  <div>
                    {intl.formatMessage({
                      id: 'MultiEditModal.requiredFieldsMessage',
                      defaultMessage: 'Following fields must be provided:',
                      description: 'Message for required fields tooltip',
                    })}
                    <ul>
                      {invalidFields.map((f) => (
                        <li key={f}>{f}</li>
                      ))}
                    </ul>
                  </div>
                ) : null
              }
              variant="primary"
              text={intl.formatMessage({
                id: 'MultiEditModal.applyChangesButton',
                defaultMessage: 'Apply Changes',
                description: 'Apply changes button text',
              })}
              onClick={this.handleApplyChangesClick}
              disabled={
                overLimit ||
                overServerLimit ||
                !fields.length ||
                !!invalidFields.length ||
                loading
              }
            />
          </>
        }
        hideModal={hideModal}
      />
    );
  }
}

MultiEditModal.propTypes = {
  fetchDocumentsCount: PropTypes.func,
  getModalData: PropTypes.func,
  update: PropTypes.func,
  onUpdateComplete: PropTypes.func,
  overLimit: PropTypes.bool,
};

export default injectSheet(styles)(
  withFlags(injectIntl(withFormatDate(MultiEditModal))),
);
